import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import "./Category.css"
import axiosInstance from "../../../Config/axiosInstance";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

import NotAuthorized from "../../../Components/ProtectedRoute/NotAuthorized";
import AddCategory from "./AddCategory";
import EditCategory from "./EditCategory";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";

const getPathFromUrl = (url) => {
  try {
    const { pathname } = new URL(url);
    return pathname;
  } catch (e) {
    console.error("Eroare la extragerea URL-ului:", e);
    return url;
  }
};

const CategoryList = ({ canCreate, canEdit, canDelete }) => {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get("/category/");
        const sortedData = response.data.sort((a, b) => b.id - a.id);
        setRows(sortedData);
      } catch (error) {
        console.error("Eroare la preluarea categoriilor:", error);
      }
    };

    fetchCategories();
  }, []);

  const handleOpenDialog = (id) => {
    setSelectedCategoryId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCategoryId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`/category/${selectedCategoryId}/`);
      setRows((prevRows) => prevRows.filter((row) => row.id !== selectedCategoryId));
    } catch (error) {
      const msg = error.response?.data?.detail || "An error occurred.";
      setErrorMessage(msg);
      setErrorDialogOpen(true);
    } finally {
      handleCloseDialog();
    }
  };

  const columns = [
    { field: "title", headerName: "Title", minWidth: 600, flex: 1, headerClassName: "bold-header" },
    { field: "subcategory_count", headerName: "Subcategories", minWidth: 400, flex: 1, headerClassName: "bold-header" },
    ...(canEdit || canDelete ? [
      {
        field: "actions",
        headerName: "Action",
        minWidth: 200,
        flex: 1,
        headerAlign: "center",
        renderCell: (params) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "2px",
              width: "100%",
              height: "100%",
            }}
          >
            {canEdit && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`/category/edit/${params.row.id}`)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}

            {canDelete && (
              <IconButton
                size="small"
                color="error"
                onClick={() => handleOpenDialog(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        ),

      }
    ] : [])
  ];

  return (
    <>
      <div className="category-page">
        <div className="header-container">
          <h2>CATEGORIES</h2>
          <div className="category-actions">
            {canCreate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                sx={{ borderRadius: "10px" }}
                onClick={() => navigate("/category/create")}
              >
                Add Category
              </Button>
            )}
          </div>
        </div>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#f0f0f0',
            borderRadius: '12px',
            padding: '4px 10px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#555',
            marginBottom: '2px',
            width: 'fit-content',
          }}
        >
          Categories&nbsp;
          <Box
            component="span"
            sx={{
              backgroundColor: '#e0e0e0',
              borderRadius: '8px',
              padding: '2px 6px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {rows.length}
          </Box>
        </Box>

        <Box className="category-box-subcontainer category-page category-datagrid">
          <div className="category-data-grid-container full-width-height custom-category-grid">
            <DataGrid
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "3px",
                  paddingBottom: "3px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />
          </div>
        </Box>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          aria-labelledby="confirm-delete-dialog"
        >
          <DialogTitle id="confirm-delete-dialog">Delete Confirmation</DialogTitle>
          <DialogContent>
            <Typography variant="h10">
              Are you sure you want to delete this category? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>CLOSE</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">CONFIRM DELETE</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          aria-labelledby="error-dialog-title"
        >
          <DialogTitle id="error-dialog-title">Error</DialogTitle>
          <DialogContent>
            <Typography>{errorMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorDialogOpen(false)} autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  )
}

const Category = () => {
  const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
  const permissions = storedPermissions?.pages || [];

  const hasPermission = (path, checkPermission) => {
    const pagePermission = permissions.some((page) => getPathFromUrl(page.url) === path && checkPermission(page));
    return pagePermission;
  };

  const canCreate = hasPermission('/category', (page) => page.can_create);
  const canEdit = hasPermission('/category', (page) => page.can_edit);
  const canDelete = hasPermission('/category', (page) => page.can_delete);

  return (
    <Routes>
      <Route path="/" element={<CategoryList canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />} />
      <Route path="create" element={canCreate ? <AddCategory /> : <NotAuthorized />} />
      <Route path="edit/:id" element={canEdit ? <EditCategory /> : <NotAuthorized />} />
    </Routes>
  );
};

export default Category;